// Ungrouped Products worklist (Screen 1) strings — EN + TR.
//
// Source: features/admin/inventory/UngroupedProductsPage.tsx.
// Screen 1 of the Variation Options & Grouping plan — cluster loose stub SKUs
// into Master Products, then map them into options (Screen 2).

import type { LocalizedDict } from '../index'

type GroupingStrings = {
  title: string
  subtitle: string
  loading: string
  loadFailed: string
  retry: string
  header: (stubCount: number, groupCount: number) => string
  skuPill: (count: number) => string
  ambiguousBadge: string
  namePlaceholder: string
  codeLabel: string
  codePlaceholder: string
  willMerge: string
  willCreateNew: string
  split: string
  createAndLink: string
  addToExisting: string
  addsToExisting: (name: string) => string
  addsToExistingNote: string
  creating: string
  removeMember: string
  ungroupedHeading: string
  ungroupedNote: string
  assignCodePlaceholder: string
  assignAdd: string
  assignedToast: (code: string) => string
  confirmStandalone: string
  keptStandaloneToast: string
  noClusters: string
  noCandidates: string
  created: (linked: number) => string
  createFailed: string
}

export const groupingI18n: LocalizedDict<GroupingStrings> = {
  en: {
    title: 'Catalog grouping',
    subtitle:
      'Review unfinished catalog candidates before linking them into product families.',
    loading: 'Finding grouping candidates…',
    loadFailed: 'Could not load grouping candidates.',
    retry: 'Retry',
    header: (stub, groups) =>
      `${stub} unfinished candidates · ${groups} suggested groups`,
    skuPill: (n) => `${n} SKUs`,
    ambiguousBadge: 'review',
    namePlaceholder: 'Master Product name',
    codeLabel: 'Code',
    codePlaceholder: 'Product code',
    willMerge: '→ merges into existing',
    willCreateNew: '→ creates a new product',
    split: 'Split',
    createAndLink: 'Create & link',
    addToExisting: 'Add to existing',
    addsToExisting: (name) => `→ Adds to existing: ${name}`,
    addsToExistingNote:
      'These SKUs will be added to the existing product. Edit the code to create a new product instead.',
    creating: 'Creating…',
    removeMember: 'Remove from group',
    ungroupedHeading: 'Individual review',
    ungroupedNote: 'Assign to an existing product or confirm as standalone.',
    assignCodePlaceholder: 'Existing product code',
    assignAdd: 'Add',
    assignedToast: (code) => `Added to ${code}`,
    confirmStandalone: 'Confirm as standalone',
    keptStandaloneToast: 'Confirmed as a standalone product',
    noClusters: 'No safe group suggested. Review the individual candidates below.',
    noCandidates: 'No unfinished grouping candidates need review.',
    created: (linked) => `Master Product created · ${linked} SKUs linked`,
    createFailed: 'Could not create the Master Product.',
  },
  tr: {
    title: 'Katalog gruplama',
    subtitle:
      'Tamamlanmamış katalog adaylarını ürün ailelerine bağlamadan önce inceleyin.',
    loading: 'Gruplama adayları bulunuyor…',
    loadFailed: 'Gruplama adayları yüklenemedi.',
    retry: 'Tekrar dene',
    header: (stub, groups) =>
      `${stub} tamamlanmamış aday · ${groups} grup önerisi`,
    skuPill: (n) => `${n} SKU`,
    ambiguousBadge: 'incele',
    namePlaceholder: 'Master Ürün adı',
    codeLabel: 'Kod',
    codePlaceholder: 'Ürün kodu',
    willMerge: '→ mevcut ürüne katılır',
    willCreateNew: '→ yeni ürün oluşturur',
    split: 'Ayır',
    createAndLink: 'Oluştur & bağla',
    addToExisting: 'Mevcuda ekle',
    addsToExisting: (name) => `→ Mevcuda eklenir: ${name}`,
    addsToExistingNote:
      'Bu SKU’lar mevcut ürüne eklenecek. Yeni ürün oluşturmak için kodu düzenleyin.',
    creating: 'Oluşturuluyor…',
    removeMember: 'Gruptan çıkar',
    ungroupedHeading: 'Tekil inceleme',
    ungroupedNote: 'Mevcut ürüne atayın veya bağımsız olduğunu onaylayın.',
    assignCodePlaceholder: 'Mevcut ürün kodu',
    assignAdd: 'Ekle',
    assignedToast: (code) => `${code} ürününe eklendi`,
    confirmStandalone: 'Bağımsız ürün olarak onayla',
    keptStandaloneToast: 'Bağımsız ürün olarak onaylandı',
    noClusters: 'Güvenli grup önerisi yok. Aşağıdaki adayları tek tek inceleyin.',
    noCandidates: 'İncelenecek tamamlanmamış gruplama adayı yok.',
    created: (linked) => `Master Ürün oluşturuldu · ${linked} SKU bağlandı`,
    createFailed: 'Master Ürün oluşturulamadı.',
  },
}

// Nav labels (AdminShell sidebar) — kept beside the page dict so the worklist
// owns all its strings.
type GroupingNavStrings = {
  ungroupedProducts: string
}

export const groupingNavI18n: LocalizedDict<GroupingNavStrings> = {
  en: { ungroupedProducts: 'Catalog grouping' },
  tr: { ungroupedProducts: 'Katalog gruplama' },
}
